import { AssetsDirectory, CasinoGameTag, TranslationScopes } from "@finbackoffice/enums";
import { ILayoutsCasinoSectionsFilterConfig, CasinoFilterTypeEnum } from "@finbackoffice/fe-core";
import { useSearchParamsQuery } from "@finbackoffice/site-core";
import classNames from "classnames";
import Button from "components/base/button/Button";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import { useCasinoFilters } from "hooks";
import { FC, useCallback } from "react";
import styles from "./sidebar-casino-filter.module.sass";

type IProps = {
    filter: ILayoutsCasinoSectionsFilterConfig;
    classname?: string;
};

const SidebarCasinoFilter: FC<IProps> = ({ filter, classname }) => {
    const { query: queryValues } = useSearchParamsQuery();

    const { onGameTagChange, onGameTypeChange, onGamePropChange } = useCasinoFilters();

    const renderFilter = useCallback(
        (filter: ILayoutsCasinoSectionsFilterConfig) => {
            let content = null;
            if (filter.enable) {
                switch (filter.type) {
                    case CasinoFilterTypeEnum.Tag: {
                        const query = { ...queryValues };
                        delete query.tag;
                        const tag = filter.value as CasinoGameTag;
                        const activeTag = queryValues.tag === tag;

                        content = (
                            <Button
                                scrollIntoView={activeTag}
                                type="custom"
                                onClick={() => onGameTagChange(tag)}
                                className={classNames(
                                    styles.filter,
                                    classname,
                                    activeTag && styles.selected,
                                )}>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Filters}/${tag.toLowerCase()}.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate
                                        tid={`casino_${tag.toLowerCase()}`}
                                        namespace={TranslationScopes.Casino}
                                    />
                                </span>
                            </Button>
                        );
                        break;
                    }
                    case CasinoFilterTypeEnum.Prop: {
                        const prop = filter.value;
                        const activeProp = queryValues.filter === prop;

                        content = (
                            <Button
                                scrollIntoView={activeProp}
                                type="custom"
                                onClick={() =>
                                    queryValues.filter !== prop && onGamePropChange(prop)
                                }
                                className={classNames(
                                    styles.filter,
                                    classname,
                                    activeProp && styles.selected,
                                )}>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Filters}/${prop}.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate
                                        tid={`casino_${prop}`}
                                        namespace={TranslationScopes.Casino}
                                    />
                                </span>
                            </Button>
                        );
                        break;
                    }
                    case CasinoFilterTypeEnum.Type: {
                        const gameType = filter.value;
                        content = (
                            <Button
                                type="custom"
                                onClick={() =>
                                    queryValues.filter !== gameType && onGameTypeChange(gameType)
                                }
                                className={classNames(
                                    styles.filter,
                                    classname,
                                    queryValues.filter === gameType && styles.selected,
                                )}>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Casino}/${AssetsDirectory.Filters}/${gameType}.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate
                                        tid={`casino_${gameType.replaceAll("-", "_")}`}
                                        namespace={TranslationScopes.Casino}
                                    />
                                </span>
                            </Button>
                        );
                        break;
                    }
                    default:
                        content = null;
                        break;
                }
            }

            return content;
        },
        [queryValues, classname, onGameTagChange, onGamePropChange, onGameTypeChange],
    );
    return renderFilter(filter);
};

export default SidebarCasinoFilter;
